import { z } from 'zod';
import { uuidSchema } from './common';

export const ORDER_SHIPMENT_KINDS = ['PARCEL', 'DIRECT'] as const;
export type OrderShipmentKind = (typeof ORDER_SHIPMENT_KINDS)[number];
export const MAX_DIRECT_METHOD_NOTE_LENGTH = 200;

const trackingNumberSchema = z
  .string()
  .transform((value) => value.replace(/[^0-9]/g, ''))
  .pipe(z.string().regex(/^[0-9]{6,30}$/, '송장 번호는 숫자 6~30자리여야 합니다.'));
const courierCodeSchema = z.string().trim().min(1, '택배사를 선택해 주세요.');
const directMethodNoteSchema = z.string().trim().min(1).max(MAX_DIRECT_METHOD_NOTE_LENGTH);

/**
 * 택배사 코드의 지원 여부는 Task 5의 목록으로 검사한다.
 * `destinationId` 는 송장을 보낼 주문 배송지(SUPER-86) — 그 주문의 배송지인지는 서버가 잠금 뒤 다시 읽어 검사한다.
 */
export const orderShipmentInputSchema = z.discriminatedUnion('kind', [
  z.strictObject({
    kind: z.literal('PARCEL'),
    destinationId: uuidSchema,
    courierCode: courierCodeSchema,
    trackingNumber: trackingNumberSchema,
  }),
  z.strictObject({
    kind: z.literal('DIRECT'),
    destinationId: uuidSchema,
    directMethodNote: directMethodNoteSchema,
  }),
]);
export type OrderShipmentInput = z.infer<typeof orderShipmentInputSchema>;

/**
 * 택배조회API 웹훅의 추적 상태 코드(`currentStatus`·진행 이력 `statusCode`) — api 웹훅 입력 검증과 화면 이름 표의 단일 원천.
 * 저장 값(`order_shipments.tracking_status`)은 문자열이라 표에 없는 코드가 남아 있을 수 있다(`getOrderTrackingStatusName` 이 null).
 */
export const ORDER_TRACKING_STATUSES = [
  'PENDING',
  'REGISTERED',
  'PICKUP_READY',
  'PICKED_UP',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'FAILED',
  'RETURNED',
  'CANCELLED',
  'HOLD',
  'UNKNOWN',
] as const;
export type OrderTrackingStatus = (typeof ORDER_TRACKING_STATUSES)[number];

/** 고객·운영자 화면의 추적 상태 이름(주문 spec §7-4 의 "반송" 등 화면 용어). 코드 원문은 화면에 내지 않는다. */
const ORDER_TRACKING_STATUS_NAMES: Readonly<Record<OrderTrackingStatus, string>> = {
  PENDING: '접수 대기',
  REGISTERED: '송장 등록',
  PICKUP_READY: '집하 준비',
  PICKED_UP: '집하 완료',
  IN_TRANSIT: '배송 중',
  OUT_FOR_DELIVERY: '배송 출발',
  DELIVERED: '배송 완료',
  FAILED: '배송 실패',
  RETURNED: '반송',
  CANCELLED: '배송 취소',
  HOLD: '배송 보류',
  UNKNOWN: '상태 확인 중',
};

const isOrderTrackingStatus = (code: string): code is OrderTrackingStatus =>
  (ORDER_TRACKING_STATUSES as readonly string[]).includes(code);

/** 추적 상태 코드의 화면 이름. 표에 없는 코드(제공자가 새로 더한 값 등)는 null — 화면이 대체 문구를 고른다. */
export function getOrderTrackingStatusName(code: string): string | null {
  return isOrderTrackingStatus(code) ? ORDER_TRACKING_STATUS_NAMES[code] : null;
}

/** 외부 응답 중 저장·표시에 허용된 필드만 유지한다. */
export const orderTrackingProgressSchema = z.object({
  dateTime: z.iso.datetime({ offset: true }),
  location: z.string(),
  status: z.string(),
  statusCode: z.string(),
  description: z.string(),
});
export type OrderTrackingProgress = z.infer<typeof orderTrackingProgressSchema>;

const shipmentMetadata = {
  id: uuidSchema,
  /** 송장이 속한 주문 배송지(SUPER-86) — 관리자·고객 송장 모두 싣는다(고객 화면이 배송지별로 묶는다). */
  destinationId: uuidSchema,
  trackingRequestId: z.string().nullable(),
  trackingRegisteredAt: z.iso.datetime().nullable(),
  trackingRegisterError: z.string().nullable(),
  trackingStatus: z.string().nullable(),
  trackingProgresses: z.array(orderTrackingProgressSchema).nullable(),
  trackingCheckedAt: z.iso.datetime().nullable(),
  deliveredAt: z.iso.datetime().nullable(),
  createdBy: uuidSchema,
  /** 등록한 운영자의 표시 이름 — 탈퇴한 계정은 '탈퇴한 계정'. 관리자 화면 전용(고객 투영은 뺀다). */
  createdByName: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
};
export const orderShipmentSchema = z.discriminatedUnion('kind', [
  z.object({
    ...shipmentMetadata,
    kind: z.literal('PARCEL'),
    courierCode: courierCodeSchema,
    trackingNumber: z.string().regex(/^[0-9]{6,30}$/),
    directMethodNote: z.null(),
  }),
  z.object({
    ...shipmentMetadata,
    kind: z.literal('DIRECT'),
    courierCode: z.null(),
    trackingNumber: z.null(),
    directMethodNote: directMethodNoteSchema,
    trackingRequestId: z.null(),
  }),
]);
export type OrderShipment = z.infer<typeof orderShipmentSchema>;

/**
 * 고객 화면(내 주문 상세)의 송장 — 관리자 송장에서 등록한 운영자(`createdBy`·`createdByName`)·택배조회 추적 요청 ID·추적 등록 시각·
 * 등록 오류를 뺀다. 추적 등록 실패와 다시 추적은 운영자만 처리한다(주문 spec §7-2·§7-4). 시도 ID 는 관리자 응답에도 없다.
 * 파싱하면 계약 밖 칸을 지우므로(zod object 기본 동작) 관리자 송장을 그대로 넘겨도 고객 송장이 된다.
 */
const customerShipmentOmit = {
  createdBy: true,
  createdByName: true,
  trackingRequestId: true,
  trackingRegisteredAt: true,
  trackingRegisterError: true,
} as const;
export const customerOrderShipmentSchema = z.discriminatedUnion('kind', [
  orderShipmentSchema.options[0].omit(customerShipmentOmit),
  orderShipmentSchema.options[1].omit(customerShipmentOmit),
]);
export type CustomerOrderShipment = z.infer<typeof customerOrderShipmentSchema>;

export const shipOrderInputSchema = z.object({
  orderId: uuidSchema,
  shipments: z.array(orderShipmentInputSchema).min(1),
});
export type ShipOrderInput = z.infer<typeof shipOrderInputSchema>;
export const addOrderShipmentsInputSchema = shipOrderInputSchema;
export type AddOrderShipmentsInput = z.infer<typeof addOrderShipmentsInputSchema>;
export const removeOrderShipmentInputSchema = z.object({ shipmentId: uuidSchema });
export type RemoveOrderShipmentInput = z.infer<typeof removeOrderShipmentInputSchema>;
export const retryOrderTrackingInputSchema = removeOrderShipmentInputSchema;
export type RetryOrderTrackingInput = z.infer<typeof retryOrderTrackingInputSchema>;
